/**
 * Einklapp-Zustand der Kopfbänder (Demo-/Trial-Banner) — reine Logik, ohne DOM.
 *
 * Teil der Rahmen-Diät (T1, Kapitel 5 Variante A): der Demo-Banner kostet am Telefon
 * 80,6 px, also 9,6 % des Bildschirms, **dauerhaft**. Der Grund ist nicht die Textmenge,
 * sondern `flex-wrap: wrap` unter 480 px — der Knopf rutscht in eine zweite Zeile.
 * Eingeklappt trägt eine 36-px-Zeile denselben Countdown und denselben Knopf.
 *
 * **Eingeklappt ist der Grundzustand** (Entscheidung vom 04.09.2026). Vorher war es
 * umgekehrt gebaut: erster Aufruf voll, danach automatisch kompakt. Das hatte einen
 * geschäftlichen Grund — der Banner ist Werbefläche für die Registrierung — kostete aber
 * die Ruhe des ersten Bildschirms genau dort, wo am Telefon ohnehin am wenigsten Platz
 * ist. Der Countdown und „Jetzt registrieren" bleiben in der kompakten Zeile sichtbar;
 * verborgen sind nur Titel und Erklärsatz, und die stehen nach einem Tipp auf den
 * Chevron wieder da.
 */

/** i18n-Schlüssel der Einklapp-/Ausklapp-Bedienung. */
export const BANNER_COLLAPSE_KEY = 'BANNER.COLLAPSE';
export const BANNER_EXPAND_KEY = 'BANNER.EXPAND';

/**
 * localStorage-Schlüssel je Band. Versioniert (`.v2`), damit ein Bedeutungswechsel des
 * Wertes am Namen ablesbar ist — siehe Warnung unten.
 *
 * ⚠️ **Der Schlüssel speichert „ausgeklappt", nicht „eingeklappt"** — deshalb `.v2` und
 * der neue Name. Gespeichert wird also der *Abweichungs*wille des Nutzers, nicht der
 * Grundzustand. Wer nur den Wert umdeutet, ohne den Namen zu wechseln, hinterlässt
 * Altwerte, die genau das Gegenteil bedeuten: aus jedem alten „eingeklappt" würde ein
 * „ausgeklappt".
 */
export const DEMO_BANNER_EXPANDED_KEY = 'fixplaner.demoBanner.expanded.v2';
export const TRIAL_BANNER_EXPANDED_KEY = 'fixplaner.trialBanner.expanded.v2';

/** Die `.v1`-Schlüssel der Vorgängerlogik. Werden beim Start entfernt, siehe oben. */
export const LEGACY_BANNER_KEYS = [
  'fixplaner.demoBanner.collapsed.v1',
  'fixplaner.trialBanner.collapsed.v1',
] as const;

/**
 * Ist das Band eingeklappt?
 *
 * @param ausgeklapptGespeichert Wert aus dem Speicher (`null`, wenn nichts gespeichert
 *   oder der Speicher gesperrt ist).
 * @param sitzungsKennung Kennung der laufenden Sitzung — beim Demo-Banner der
 *   Ablaufzeitpunkt (`DemoService.LS_EXPIRES_AT`), beim Trial-Banner `'1'`.
 * @param istTelefonstufe Nur am Telefon gibt es die kompakte Zeile überhaupt.
 *
 * **Grundzustand am Telefon ist eingeklappt.** Ausgeklappt wird nur, wenn der Nutzer es
 * in *dieser* Sitzung selbst gewählt hat.
 *
 * ⚠️ **Der gespeicherte Wert ist die Sitzungskennung, nicht `'1'`.** Damit gilt die
 * Entscheidung für *diese* Demo: eine neue Demo hat einen neuen Ablaufzeitpunkt und
 * beginnt wieder im Grundzustand. Kein Aufräum-Job nötig, und der Zustand übersteht
 * Reload und Tabs.
 *
 * ⚠️ **`sessionStorage` wäre falsch,** obwohl es „eine Sitzung" heißt: eine Demo läuft
 * 48 Stunden und übersteht Reloads, Tabs und Neustarts. Wer aufklappt, will es nicht nur
 * für diesen Tab.
 */
export function resolveBannerCollapsed(
  ausgeklapptGespeichert: string | null,
  sitzungsKennung: string | null,
  istTelefonstufe: boolean
): boolean {
  if (!istTelefonstufe) return false;
  if (!ausgeklapptGespeichert || !sitzungsKennung) return true;
  return ausgeklapptGespeichert !== sitzungsKennung;
}

/**
 * Liest einen Schlüssel, ohne bei gesperrtem Speicher zu werfen.
 *
 * Rückfall ist `null` — am Telefon also der Grundzustand „eingeklappt", auf allen
 * anderen Stufen „sichtbar wie immer".
 */
export function readBannerFlag(schluessel: string): string | null {
  try {
    return localStorage.getItem(schluessel);
  } catch {
    return null;
  }
}

/** Schreibt bzw. löscht den Ausklapp-Willen. Fehler bleiben ohne Folgen. */
export function writeBannerFlag(schluessel: string, wert: string | null): void {
  try {
    if (wert === null) localStorage.removeItem(schluessel);
    else localStorage.setItem(schluessel, wert);
  } catch {
    // Privatmodus / Speicher gesperrt — der Zustand gilt dann nur fuer diesen Aufruf.
  }
}

/** Entfernt die `.v1`-Altwerte, deren Bedeutung invertiert war. */
export function dropLegacyBannerFlags(): void {
  for (const schluessel of LEGACY_BANNER_KEYS) writeBannerFlag(schluessel, null);
}
